import { useModal } from '@europrocurement/flexy-components';
import { Box, Button, CircularProgress, useTheme } from '@mui/material';
import React from 'react';

type ButtonFooterFormalistAssignModalProps = {
    isAssignButtonEnabled?: boolean;
    handleAssignFormaliste: () => void;
    /** When provided, enables dossier mode: shows two action buttons instead of one
     *  Used to assign formalist to one dossier or multiples dossier if collection has more than one dossier
     */
    handleAssignFormalisteAll?: () => void;
    isLoading?: boolean;
    submitButtonText?: string;
};

const ButtonFooterFormalistAssignModal: React.FunctionComponent<ButtonFooterFormalistAssignModalProps> =
    function ({
        handleAssignFormaliste,
        handleAssignFormalisteAll,
        isLoading,
        isAssignButtonEnabled,
        submitButtonText,
    }) {
        const { palette } = useTheme();
        const { modalActions } = useModal();

        const isDossierMode = !!handleAssignFormalisteAll;

        const loadingButtonSx = {
            padding: '16px 24px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minWidth: '6rem',
            fontSize: '16px',
        };

        const containedButtonSx = {
            padding: '16px 24px',
            fontSize: '16px',
            transition: 'all ease-in-out 300ms',
            backgroundColor: isAssignButtonEnabled ? palette.primary.main : palette.grey[300],
            ':hover': {
                backgroundColor: isAssignButtonEnabled ? palette.primary.dark : palette.grey[300],
            },
        };

        const renderActionButtons = () => {
            if (isLoading) {
                return (
                    <>
                        {isDossierMode && (
                            <Button
                                variant="outlined"
                                disabled
                                sx={loadingButtonSx}
                            >
                                <CircularProgress
                                    color="primary"
                                    sx={{ maxHeight: '15px', maxWidth: '15px' }}
                                />
                            </Button>
                        )}
                        <Button
                            variant="contained"
                            disabled
                            sx={loadingButtonSx}
                        >
                            <CircularProgress
                                color="info"
                                sx={{ maxHeight: '15px', maxWidth: '15px', color: 'common.white' }}
                            />
                        </Button>
                    </>
                );
            }

            return (
                <>
                    {isDossierMode && (
                        <Button
                            onClick={handleAssignFormaliste}
                            variant="outlined"
                            disabled={!isAssignButtonEnabled}
                            sx={{ padding: '16px 24px', fontSize: '16px' }}
                        >
                            Assigner pour le dossier en cours
                        </Button>
                    )}
                    <Button
                        onClick={isDossierMode ? handleAssignFormalisteAll : handleAssignFormaliste}
                        variant="contained"
                        disabled={!isAssignButtonEnabled}
                        sx={containedButtonSx}
                    >
                        {isDossierMode
                            ? 'Assigner pour tous les dossiers de la commande'
                            : submitButtonText}
                    </Button>
                </>
            );
        };

        return (
            <Box
                sx={{
                    display: 'flex',
                    flexWrap: isDossierMode ? 'wrap' : undefined,
                    gap: '16px',
                    justifyContent: isDossierMode ? 'center' : undefined,
                }}
            >
                <Button
                    onClick={() => modalActions.close()}
                    variant="outlined"
                    disabled={isLoading}
                    sx={{ padding: '16px 24px', fontSize: '16px' }}
                >
                    Annuler
                </Button>
                {renderActionButtons()}
            </Box>
        );
    };

export default ButtonFooterFormalistAssignModal;
